import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { ToastController } from '@ionic/angular';
import {
  Action,
  Actions,
  ofActionSuccessful,
  Selector,
  State,
  StateContext,
  Store
} from '@ngxs/store';
import {
  catchError,
  EMPTY,
  retry,
  switchMap,
  take,
  tap,
  throwError
} from 'rxjs';

import {
  CreditCardProcessingType,
  PaymentRequestType,
  TicketStatus
} from 'src/app/models';
import { PaymentService, TicketService } from 'src/app/services';
import { GlobalService } from 'src/app/services/global/global.service';
import { ExceptionError, HttpRequestError } from '../error/error.action';
import {
  LoadTicketDetailSuccess,
  MealPlanPaymentError,
  RemoveTicketSuccess,
  UpdateTicketSuccess
} from '../tickets/tickets.action';
import {
  CancelGatewayTransaction,
  CancelGatewayTransactionSuccess,
  FinalizeMealPlanTicket,
  FinalizeMealPlanTicketError,
  FinalizeMealPlanTicketSuccess,
  GetBusinessLines,
  GetDepartments,
  GetGiftCard,
  GetGiftCardSuccess,
  MakePayment,
  MakePaymentError,
  MakePaymentFailed,
  MakePaymentGatewayTransactionSuccess,
  MakePaymentSuccess,
  OnGatewayTransactionError,
  OnGatewayTransactionSuccess,
  OnRetryGatewayTransaction,
  PayOnPickup,
  PayOnPickupError,
  PayOnPickupSuccess,
  ResetDepartments
} from './payment.action';
import { LOG_ROCKET_CUSTOM_EVENTS, LogRocketProvider } from 'src/app/providers';
import { TICKETS_STATE_TOKEN } from 'src/app/store/tickets/tickets.state';
import { PAYMENT_STATE_TOKEN, PaymentStateModel } from './payment.state.model';
import { LOCATION_STATE_TOKEN } from 'src/app/store/location/location.state';
import { StartIdleTime, StopIdle } from 'src/app/store/app/app.action';
import { TranslocoService } from '@ngneat/transloco';

@State({
  name: PAYMENT_STATE_TOKEN,
  defaults: {
    departments: [],
    businessLines: [],
    selectedGiftCard: null,
    lastGatewayTransaction: null
  }
})
@Injectable()
export class PaymentState {
  errorMsg: string;

  constructor(
    private store: Store,
    private gbService: GlobalService,
    private ticketService: TicketService,
    private paymentService: PaymentService,
    private logRocketProvider: LogRocketProvider,
    private toastController: ToastController,
    private ngZone: NgZone,
    private action$: Actions,
    private translocoService: TranslocoService
  ) {}

  @Selector()
  static businessLines(state: PaymentStateModel) {
    return state.businessLines;
  }

  @Selector()
  static departments(state: PaymentStateModel) {
    return state.departments;
  }

  @Selector()
  static lastGatewayTransaction(state: PaymentStateModel) {
    return state.lastGatewayTransaction;
  }

  //#region Actions

  //Call the request to get the Departments
  @Action(GetBusinessLines)
  getBusinessLines({ patchState }: StateContext<PaymentStateModel>) {
    return this.gbService
      .getBusinessLines()
      .pipe(tap((businessLines) => patchState({ businessLines })));
  }

  //Call the request to get the Departments
  @Action(GetDepartments)
  getDepartments(
    { patchState }: StateContext<PaymentStateModel>,
    { search_text, business_line_id }: GetDepartments
  ) {
    return this.gbService
      .getDepartments(search_text, business_line_id)
      .pipe(tap((departments) => patchState({ departments })));
  }

  @Action(ResetDepartments)
  resetDepartments({ patchState }: StateContext<PaymentStateModel>) {
    patchState({ departments: [] });
  }

  //Call the request to make the payment
  @Action(MakePayment)
  makePayment(
    ctx: StateContext<PaymentStateModel>,
    { ticket, payment_request, closed_from_source }: MakePayment
  ) {
    if (ticket) {
      const isCreditCardProcessing =
        payment_request.type === PaymentRequestType.CREDIT_CARD &&
        this.store.selectSnapshot(LOCATION_STATE_TOKEN).location
          .credit_card_processing !== CreditCardProcessingType.None;

      if (isCreditCardProcessing) {
        ctx.dispatch(new StopIdle());
        this.action$
          .pipe(
            ofActionSuccessful(
              MakePaymentFailed,
              OnGatewayTransactionSuccess,
              OnGatewayTransactionError
            ),
            take(1)
          )
          .subscribe(() => ctx.dispatch(new StartIdleTime()));

        return this.ticketService
          .makePaymentGatewayTransaction({
            ticket_uuid: ticket.device_ticket_uuid,
            paymentRequest: payment_request,
            closed_from_source
          })
          .pipe(
            tap((result) =>
              ctx.dispatch(
                new MakePaymentGatewayTransactionSuccess(
                  result,
                  ticket,
                  payment_request
                )
              )
            ),
            catchError((error: HttpErrorResponse) =>
              ctx.dispatch(
                new MakePaymentFailed(error, { ticket, payment_request })
              )
            )
          );
      }

      return this.ticketService
        .makePaymentTransactions({
          payment_request,
          closed_from_source,
          ticket_uuid: ticket.device_ticket_uuid
        })
        .pipe(
          tap((response) => {
            this.logRocketProvider.track(
              LOG_ROCKET_CUSTOM_EVENTS.Transaction.Complete,
              {
                device_ticket_uuid: ticket.device_ticket_uuid,
                type: payment_request.type
              }
            );

            const actions: any[] = [
              new MakePaymentSuccess(
                ticket.device_ticket_uuid,
                response,
                payment_request
              )
            ];

            if (response.ticket.status !== TicketStatus.CLOSED) {
              actions.push(new LoadTicketDetailSuccess(response.ticket));
            }

            ctx.dispatch(actions);
          }),
          catchError((error: HttpErrorResponse) =>
            ctx.dispatch(
              new MakePaymentFailed(error, { ticket, payment_request })
            )
          )
        );
    }
  }

  @Action(MakePaymentGatewayTransactionSuccess)
  makePaymentGatewayTransactionSuccess(
    { patchState, getState, dispatch }: StateContext<PaymentStateModel>,
    { result, ticket, paymentRequest }: MakePaymentGatewayTransactionSuccess
  ) {
    this.logRocketProvider.track(
      LOG_ROCKET_CUSTOM_EVENTS.Transaction.InProgress,
      {
        device_ticket_uuid: ticket.device_ticket_uuid,
        type: paymentRequest.type
      }
    );
    patchState({
      lastGatewayTransaction: {
        ticket,
        paymentRequest,
        result,
        stripe_payment_secret: '',
        stripe_pk_production: '',
        stripe_pk_test: '',
        stripe_account_id: ''
      }
    });

    const selectedTicketId =
      this.store.selectSnapshot(TICKETS_STATE_TOKEN).selectedTicket.data;

    return this.ticketService
      .getTrancloudTransaction(selectedTicketId, result.gateway_transaction_id)
      .pipe(
        tap((response) => {
          switch (response.status) {
            case 'in_progress':
              const lastGatewayTransaction = getState().lastGatewayTransaction;
              const isStripePaymentSecretSet =
                !!lastGatewayTransaction?.stripe_payment_secret;

              //We only need to set this once during this transaction
              if (response.stripe_payment_secret && !isStripePaymentSecretSet) {
                patchState({
                  lastGatewayTransaction: {
                    ...lastGatewayTransaction,
                    stripe_payment_secret: response.stripe_payment_secret,
                    stripe_pk_test: response.stripe_pk_test,
                    stripe_pk_production: response.stripe_pk_production,
                    stripe_account_id: response.stripe_account_id
                  }
                });
              }
              throw response;
            case 'complete':
              this.logRocketProvider.track(
                LOG_ROCKET_CUSTOM_EVENTS.Transaction.Complete,
                {
                  device_ticket_uuid: selectedTicketId,
                  transaction_id: response.transaction?.id,
                  type: response.transaction?.type
                }
              );
              dispatch([
                new OnGatewayTransactionSuccess(),
                new MakePaymentSuccess(
                  selectedTicketId,
                  {
                    transaction: response.transaction,
                    ticket_status: response.ticket_status,
                    new_balance: response.new_balance
                  },
                  paymentRequest
                )
              ]);
              break;
            default:
              this.logRocketProvider.track(
                LOG_ROCKET_CUSTOM_EVENTS.Transaction.Declined,
                {
                  device_ticket_uuid: selectedTicketId
                }
              );

              //Default Gateway Transaction error
              let error = this.translocoService.translate('errors.gatewayTransactionProblem');

              // Gateway Transaction payment_gateway_response value will be different depending on the payment processor:
              // Stripe - String value returned with readable error
              // Trancloud - Object returned, readable error within Rstream.TextResponse
              const payment_gateway_response =
                response?.payment_gateway_response;
              const RStream =
                payment_gateway_response?.RStream ||
                payment_gateway_response?.Rstream;
              const CmdStatus = RStream?.CmdStatus;
              const TextResponse = RStream?.TextResponse;

              if (typeof payment_gateway_response === 'string') {
                error = payment_gateway_response;
              } else if (TextResponse) {
                error = TextResponse;
              }

              dispatch(new OnGatewayTransactionError(error, CmdStatus));
          }
        }),
        catchError((e: HttpErrorResponse) => {
          //Action should only be dispatched if there is an actual backend error response.
          //This catchError is triggered when response status is in_progress due to the throw on the above Switch Case to trigger the retry operator.
          if (e.error?.message === 'failed') {
            dispatch(
              new OnGatewayTransactionError(this.translocoService.translate('errors.gatewayTransactionCancelled'))
            );
            return EMPTY;
          }

          return throwError(() => e);
        }),
        retry({ delay: 2000 })
      );
  }

  @Action(MakePaymentFailed)
  makePaymentFailed(
    ctx: StateContext<PaymentStateModel>,
    { error, paymentData }: MakePaymentFailed
  ) {
    const isCreditCard =
      paymentData.payment_request.type === PaymentRequestType.CREDIT_CARD;
    if (isCreditCard) {
      ctx.patchState({
        lastGatewayTransaction: {
          ticket: paymentData.ticket,
          paymentRequest: paymentData.payment_request
        }
      });
    }

    const errorMessage: string = error.error?.message;
    return ctx.dispatch(
      errorMessage?.includes('Insufficient mealplan')
        ? new MealPlanPaymentError(
            paymentData.ticket.device_ticket_uuid,
            error.error?.message
          )
        : new MakePaymentError(error, paymentData)
    );
  }

  @Action(MakePaymentError)
  makePaymentError(
    ctx: StateContext<PaymentStateModel>,
    { error: httpError, posPaymentData }: MakePaymentError
  ) {
    const paymentRequest = posPaymentData.payment_request;
    const { status, error } = httpError;
    const httpErrorMsg = error?.message;
    let errorMessage =
      httpErrorMsg ||
      this.translocoService.translate('errors.paymentProcessingError');

    if (paymentRequest.type === PaymentRequestType.CHARGE_ACCOUNT) {
      if (status === 422) {
        errorMessage = this.translocoService.translate('errors.insufficientFunds', { errorMsg: httpErrorMsg });
      }
    }

    ctx.dispatch(new HttpRequestError(errorMessage));
  }

  @Action(OnRetryGatewayTransaction)
  retryGatewayTransaction(ctx: StateContext<PaymentStateModel>) {
    const { lastGatewayTransaction } = ctx.getState();

    return ctx.dispatch(
      lastGatewayTransaction
        ? new MakePayment(
            lastGatewayTransaction.ticket,
            lastGatewayTransaction.paymentRequest
          )
        : new ExceptionError(
            this.translocoService.translate('errors.paymentProcessingError')
          )
    );
  }

  @Action(OnGatewayTransactionSuccess)
  onGatewayTransactionSuccess(ctx: StateContext<PaymentStateModel>) {
    ctx.patchState({ lastGatewayTransaction: null });
  }

  @Action(GetGiftCard)
  getGiftCard(
    ctx: StateContext<PaymentStateModel>,
    { identification_number }: GetGiftCard
  ) {
    return this.gbService.getGiftCard(identification_number).pipe(
      tap((gift_card) => {
        ctx.dispatch(new GetGiftCardSuccess(gift_card));
      })
    );
  }

  @Action(CancelGatewayTransaction)
  onCancelGatewayTransaction({
    getState,
    dispatch
  }: StateContext<PaymentStateModel>) {
    const { ticket, result } = getState().lastGatewayTransaction;

    return this.paymentService
      .cancelGatewayTransaction(
        ticket.device_ticket_uuid,
        result.gateway_transaction_id
      )
      .pipe(
        tap(({ success, payment_received, error_message }) => {
          dispatch(
            success
              ? new CancelGatewayTransactionSuccess()
              : new ExceptionError(
                  payment_received
                    ? error_message
                    : this.translocoService.translate('errors.cancelGatewayTransactionProblem')
                )
          );
        }),
        catchError(() =>
          dispatch(
            new ExceptionError(
              this.translocoService.translate('errors.cancelGatewayTransactionProblem')
            )
          )
        )
      );
  }

  @Action(PayOnPickup)
  payOnPickup(
    ctx: StateContext<PaymentStateModel>,
    { deviceTicketUuid }: PayOnPickup
  ) {
    return this.paymentService.payOnPickup(deviceTicketUuid).pipe(
      tap((res) => ctx.dispatch(new PayOnPickupSuccess(res))),
      catchError(() =>
        ctx.dispatch(
          new PayOnPickupError(this.translocoService.translate('errors.problemPlacingOrder'))
        )
      )
    );
  }

  @Action(PayOnPickupSuccess)
  payOnPickupSuccess(
    ctx: StateContext<PaymentStateModel>,
    { ticket }: PayOnPickupSuccess
  ) {
    const isAutoCloseTicket =
      this.store.selectSnapshot(LOCATION_STATE_TOKEN).location
        .auto_close_on_full_payment;

    this.toastPlaceOrderSuccess();

    if (isAutoCloseTicket) {
      return ctx.dispatch(new RemoveTicketSuccess(ticket.device_ticket_uuid));
    }

    return ctx.dispatch(new UpdateTicketSuccess(ticket));
  }

  @Action(FinalizeMealPlanTicket)
  finalizeMealPlanTicket(
    { dispatch }: StateContext<PaymentStateModel>,
    { deviceTicketUuid }: FinalizeMealPlanTicket
  ) {
    return this.paymentService.finalizeMealPlanTicket(deviceTicketUuid).pipe(
      switchMap((res) =>
        res.success
          ? dispatch(new FinalizeMealPlanTicketSuccess(res.ticket))
          : dispatch(
              new FinalizeMealPlanTicketError(
                this.translocoService.translate('errors.problemPlacingOrder')
              )
            )
      ),
      catchError((error: HttpErrorResponse) =>
        dispatch(
          new FinalizeMealPlanTicketError(
            this.translocoService.translate('errors.problemPlacingOrder'),
            error
          )
        )
      )
    );
  }

  @Action(FinalizeMealPlanTicketSuccess)
  finalizeMealPlanTicketSuccess(
    { dispatch }: StateContext<PaymentStateModel>,
    { ticket }: FinalizeMealPlanTicketSuccess
  ) {
    return dispatch(
      new RemoveTicketSuccess(ticket.device_ticket_uuid)
    ).subscribe(() => this.toastPlaceOrderSuccess());
  }
  //#endregion

  private toastPlaceOrderSuccess() {
    this.ngZone.run(async () => {
      const toast = await this.toastController.create({
        color: 'success',
        message: this.translocoService.translate('messages.orderPlacedSuccessfully'),
        duration: 4000
      });
      await toast.present();
    });
  }
}
